import React, { useEffect } from 'react';
import { theme } from '../../theme';
import { useMessageGetter } from 'react-message-context';
import Card from '../../components/common/Card';
import styled from 'styled-components/macro';
import { NotificationType, useApp, useAppDispatch } from '../../context/appContext';
import { getMenuItemPathBySlug } from '../../utils/wordpress';
import { Slugs } from '../../constants/knownSlugs';
import { FlexColumn, FlexRow } from '../../components/basic/Flex';
import { LinkButton } from '../../components/common';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loadDebt } from '../../context/kennoTenantActions';
import { LOAD_STATES } from '../../context/common';
import { showNotification } from '../../context/appActions';
import Loader from '../../components/basic/Loader';
import { formatPrice, isOlderThanOneWeek, shouldHideFinancialInfo } from '../../utils';
import { MaintenanceNote } from '../../components/FinancialInfoMaintenanceNote';

const Cell = styled(FlexColumn)`
  height: min-content;
`;
const CellTitle = styled.p`
  font-size: 16px;
  margin-bottom: 12px;
  font-family: ${(props) => props.theme.text.primaryFont};
`;
const CellValue = styled.p`
  font-size: 36px;
  margin: 0;
`;
const Footer = styled.p`
  margin-top: 1rem;
  font-size: 14px;
  font-family: ${(props) => props.theme.text.primaryFont};
`;

const DebtCard = () => {
  const t = useMessageGetter('home.grid.debt');
  const [{ debt }, dispatch] = useKennoTenant();
  const [{ menu }] = useApp();
  const appDispatch = useAppDispatch();

  useEffect(() => {
    loadDebt(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (debt.loadState === LOAD_STATES.ERROR) {
      showNotification(appDispatch, t('loadfailure'), NotificationType.ERROR);
    }
  }, [debt.loadState]); // eslint-disable-line react-hooks/exhaustive-deps

  const fullDebt = debt.data?.currentDue;
  const hasDebt = !!Number(fullDebt);

  const hasDueDatesExceededOneWeek = () => {
    const dueDates = debt.data?.debtByMonth.flatMap((month) => month.debtByTarget.map((target) => target.dueDate));
    return dueDates?.some((dueDate) => isOlderThanOneWeek(dueDate));
  };

  // Don't show DebtCard if all the debt has overdued less than week ago
  if (!hasDebt || !hasDueDatesExceededOneWeek()) {
    return null;
  }

  const debtContent = () => {
    if (shouldHideFinancialInfo()) {
      return <MaintenanceNote translationKey="financial.rentCard" />;
    }
    if (debt.loadState === LOAD_STATES.BUSY) {
      return <Loader />;
    }

    const formattedFullDebt = formatPrice(Number(fullDebt));

    return (
      <FlexRow wrap="wrap">
        <Cell>
          <CellTitle>{t('unpaid.label')}</CellTitle>
          <CellValue>{formattedFullDebt}</CellValue>
        </Cell>
        <Footer>{t('payment_delay_description')}</Footer>
      </FlexRow>
    );
  };

  return (
    <Card
      title={t('title')}
      background={theme().colors.strawberry}
      mainButton={
        <LinkButton
          size="small"
          background="transparent"
          to={getMenuItemPathBySlug(menu.items, Slugs.DEBT)}
          iconRight
          icon="arrowForward"
        >
          {t('action.label')}
        </LinkButton>
      }
    >
      {debtContent()}
    </Card>
  );
};

export default DebtCard;
